<template>
  <div class="faq-tab">
    <div class="faq-tab__head">
      <ds-button v-if="!formOpen" data-qa="faq-add-button" @click="startAdd">
        <ds-icon name="plus" />
        Nieuwe vraag toevoegen
      </ds-button>
      <span v-if="notification" class="faq-tab__save-ok">&#x2713; {{ notification }}</span>
    </div>

    <div v-if="formOpen" class="faq-tab__form ds-card">
      <div class="ds-card__header">{{ editingId ? 'Vraag bewerken' : 'Nieuwe vraag' }}</div>
      <div class="ds-card__body">
        <div class="ds-form">
          <div class="ds-form__field">
            <label for="faq-question">Vraag</label>
            <input
              id="faq-question"
              v-model="form.question"
              type="text"
              class="ds-field"
              placeholder="Voer de vraag in"
            />
          </div>
          <div class="ds-form__field">
            <label for="faq-answer">Antwoord</label>
            <textarea
              id="faq-answer"
              v-model="form.answer"
              class="ds-field"
              placeholder="Voer het antwoord in"
              rows="5"
            />
          </div>
          <div class="ds-form__field faq-tab__sort-field">
            <label for="faq-sort-order">Volgorde</label>
            <input
              id="faq-sort-order"
              v-model.number="form.sortOrder"
              type="number"
              class="ds-field"
              min="0"
            />
          </div>
          <div class="faq-tab__form-actions">
            <ds-button data-qa="faq-save-button" :disabled="saving" @click="save">
              <ds-icon v-if="editingId" name="edit" />
              <ds-icon v-else name="save" />
              {{ saving ? 'Opslaan…' : editingId ? 'Wijzig' : 'Opslaan' }}
            </ds-button>
            <ds-button
              variant="ghost"
              data-qa="faq-cancel-button"
              :disabled="saving"
              @click="cancelForm"
            >
              Annuleren
            </ds-button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="loading" class="ds-empty">
      <p class="ds-empty__message">Vragen laden…</p>
    </div>
    <div v-else-if="error" class="ds-empty">
      <p class="ds-empty__message">{{ error }}</p>
      <ds-button variant="ghost" data-qa="faq-retry-button" @click="load">
        Opnieuw proberen
      </ds-button>
    </div>
    <template v-else>
      <div v-if="!faqs.length && !formOpen" class="ds-empty">
        <h2 class="ds-empty__title">Geen vragen</h2>
        <p class="ds-empty__message">Er zijn nog geen veelgestelde vragen.</p>
      </div>
      <div v-else-if="faqs.length" class="ds-table">
        <div class="ds-table__scroll">
          <table>
            <thead>
              <tr>
                <th class="ds-table__header-cell">
                  <div class="ds-table__header-inner">
                    <span class="ds-table__label ds-table__label--static">Vraag</span>
                  </div>
                </th>
                <th class="ds-table__header-cell">
                  <div class="ds-table__header-inner">
                    <span class="ds-table__label ds-table__label--static">Antwoord</span>
                  </div>
                </th>
                <th class="ds-table__header-cell">
                  <div class="ds-table__header-inner">
                    <span class="ds-table__label ds-table__label--static">Volgorde</span>
                  </div>
                </th>
                <th class="ds-table__header-cell"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="faq in faqs" :key="faq.id">
                <td class="faq-tab__question">{{ faq.question }}</td>
                <td class="faq-tab__answer">{{ truncate(faq.answer) }}</td>
                <td>{{ faq.sortOrder }}</td>
                <td>
                  <div class="ds-table__actions">
                    <button
                      class="ds-icon-button"
                      title="Bewerken"
                      data-qa="faq-edit-button"
                      @click="startEdit(faq)"
                    >
                      <ds-icon name="edit" />
                    </button>
                    <button
                      class="ds-icon-button"
                      title="Verwijderen"
                      data-qa="faq-delete-button"
                      :disabled="deletingId === faq.id"
                      @click="confirmDelete(faq)"
                    >
                      <ds-icon name="trash" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <ds-confirm-dialog
      id="faq-delete"
      :show="!!deleteTarget"
      title="Vraag verwijderen"
      :body="deleteConfirmText"
      confirm-label="Verwijderen"
      cancel-label="Annuleren"
      danger
      :confirm-disabled="deletingId !== null"
      @confirm="executeDelete"
      @cancel="deleteTarget = null"
    />
  </div>
</template>

<script>
import FaqApi from '@/api/server/Faq.api.js'
import DsButton from '@/components/ds/atoms/dsButton/DsButton.vue'
import DsIcon from '@/components/ds/atoms/dsIcon/DsIcon.vue'
import DsConfirmDialog from '@/components/ds/molecules/dsConfirmDialog/DsConfirmDialog.vue'

export default {
  name: 'FaqTab',
  components: { DsButton, DsIcon, DsConfirmDialog },
  data() {
    return {
      faqs: [],
      loading: false,
      error: null,
      formOpen: false,
      editingId: null,
      form: { question: '', answer: '', sortOrder: 0 },
      saving: false,
      deleteTarget: null,
      deletingId: null,
      notification: null,
    }
  },
  computed: {
    deleteConfirmText() {
      return this.deleteTarget
        ? `Weet u zeker dat u "${this.deleteTarget.question}" wilt verwijderen?`
        : ''
    },
  },
  async mounted() {
    await this.load()
  },
  methods: {
    async load() {
      this.loading = true
      this.error = null
      try {
        const response = await FaqApi.getAllFaqs()
        this.faqs = response.data || []
      } catch {
        this.error = 'Vragen konden niet worden geladen. Probeer het opnieuw.'
      } finally {
        this.loading = false
      }
    },
    startAdd() {
      this.editingId = null
      this.form = { question: '', answer: '', sortOrder: 0 }
      this.formOpen = true
      this.notification = null
    },
    startEdit(faq) {
      this.editingId = faq.id
      this.form = { question: faq.question, answer: faq.answer, sortOrder: faq.sortOrder }
      this.formOpen = true
      this.notification = null
    },
    cancelForm() {
      this.formOpen = false
      this.editingId = null
    },
    async save() {
      this.saving = true
      try {
        const payload = { ...this.form }
        if (this.editingId) {
          payload.id = this.editingId
          await FaqApi.updateFaq(this.editingId, payload)
        } else {
          await FaqApi.createFaq(payload)
        }
        await this.load()
        this.formOpen = false
        this.editingId = null
        this.showNotification('Opgeslagen')
      } catch {
        this.error = 'Opslaan mislukt. Probeer het opnieuw.'
      } finally {
        this.saving = false
      }
    },
    confirmDelete(faq) {
      this.deleteTarget = faq
    },
    async executeDelete() {
      const faq = this.deleteTarget
      this.deletingId = faq.id
      try {
        await FaqApi.deleteFaq(faq.id)
        await this.load()
        this.showNotification('Verwijderd')
      } catch {
        this.error = 'Verwijderen mislukt. Probeer het opnieuw.'
      } finally {
        this.deletingId = null
        this.deleteTarget = null
      }
    },
    showNotification(message) {
      this.notification = message
      setTimeout(() => {
        this.notification = null
      }, 3000)
    },
    truncate(text, max = 80) {
      if (!text) return ''
      return text.length > max ? text.substring(0, max) + '…' : text
    },
  },
}
</script>

<style lang="scss" scoped>
// Layout only. The card, table, buttons, dialog and form fields come from the
// design system (styles/ds/). See styles/ds/README.md before adding anything
// here.

.faq-tab__head {
  display: flex;
  align-items: center;
  gap: var(--ds-space-4);
  margin-bottom: var(--ds-space-4);
}

.faq-tab__save-ok {
  font-size: 13.5px;
  font-weight: 700;
  color: var(--ds-color-success);
}

.faq-tab__form {
  margin-bottom: var(--ds-space-5);
}

.faq-tab__sort-field {
  max-width: 140px;
}

.faq-tab__form-actions {
  display: flex;
  align-items: center;
  gap: var(--ds-space-3);
}

// The design's FAQ table declares no column widths — with table-layout: fixed
// that means four equal columns — and top-aligns its cells so a long answer
// reads from the same line as its question.
.faq-tab .ds-table td {
  vertical-align: top;
}

.faq-tab__question {
  overflow-wrap: break-word;
}

.faq-tab__answer {
  max-width: 360px;
  overflow-wrap: break-word;
  font-style: italic;
  color: var(--ds-color-text-muted);
}
</style>
